<template>
  <v-dialog
    v-model="dialog"
    persistent
    scrollable
    max-width="600px"
  >
    <v-card style="position: relative;">
      <v-overlay
        absolute
        :value="isProcessing"
        class="text-center"
      >
        <v-progress-circular
          indeterminate
          :size="50"
          color="primary"
        />
        <p class="text-overline white--text mt-4">
          Processing...
        </p>
      </v-overlay>
      <v-card-title>
        <span class="text-h5">Change Current Team</span>
      </v-card-title>
      <v-card-text>
        <v-container>
          <v-row>
            <v-col>
              <DynamicAutoComplete
                v-if="dialog"
                v-model="selectedTeamId"
                :selected-object.sync="selectedTeam"
                fetch-url="/teams"
                label="Select Team"
                item-text="name_global"
                item-value="id"
              />
            </v-col>
          </v-row>
          <v-row v-if="selectedTeam">
            <v-col>
              <v-expansion-panels v-if="selectedTeamId && !isTeamGlobal">
                <v-expansion-panel>
                  <v-expansion-panel-header>
                    {{ selectedTeamName }}'s Records
                  </v-expansion-panel-header>
                  <v-expansion-panel-content>
                    <v-text-field
                      v-model="recordFilter"
                      label="Filter Records"
                      clearable
                      outlined
                      dense
                      hide-details
                    />
                    <v-list>
                      <v-list-item
                        v-for="record of filteredTeamRecords"
                        :key="record.id"
                        link
                        @click="_ => onSelectRecord(record)"
                      >
                        <v-list-item-icon>
                          <v-icon color="indigo">
                            folder
                          </v-icon>
                        </v-list-item-icon>

                        <v-list-item-content>
                          <v-list-item-title>{{ record.name }}</v-list-item-title>
                        </v-list-item-content>
                      </v-list-item>
                      <v-list-item
                        v-if="teamRecords.length === 0 && !isTeamGlobal"
                        class="text-center"
                      >
                        <v-list-item-content>
                          <v-alert
                            type="info"
                            outlined
                          >
                            This team has no Records associated
                          </v-alert>
                        </v-list-item-content>
                      </v-list-item>
                      <v-list-item
                        v-if="isTeamGlobal"
                        class="text-center"
                      >
                        <v-list-item-content>
                          <v-alert
                            type="warning"
                            outlined
                          >
                            This is a Global Team has access to all Records
                          </v-alert>
                        </v-list-item-content>
                      </v-list-item>
                    </v-list>
                  </v-expansion-panel-content>
                </v-expansion-panel>
              </v-expansion-panels>

              <v-list v-if="selectedTeamId && isTeamGlobal">
                <v-list-item class="text-center">
                  <v-list-item-content>
                    <v-alert
                      type="warning"
                      outlined
                    >
                      This is a Global Team has access to all Records
                    </v-alert>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
            </v-col>
          </v-row>
          <v-row v-else>
            <v-col>
              <v-skeleton-loader
                type="list-item-two-line"
              />
            </v-col>
          </v-row>
        </v-container>
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn
          color="primary"
          text
          @click="dialog = false"
        >
          Close
        </v-btn>
        <v-btn
          color="secondary"
          text
          @click="_ => onSelectTeam()"
        >
          Update
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'

import { alertHandler } from '@/alertHandler'
import { parseErrorResponseMessage } from '@/utilities'

import { USER_NAMESPACE } from '@/store/user'

import DynamicAutoComplete from '@/components/Fields/DynamicAutoComplete.vue'

export default {
  name: 'ChangeCurrentTeamDialog',

  components: {
    DynamicAutoComplete,
  },

  props: {
    value: {
      type: Boolean,
      required: true,
    },
  },

  setup () {
    return {
      ...alertHandler(),
    }
  },

  data () {
    return {
      isProcessing: false,
      recordFilter: null,
      selectedTeam: null,
      selectedTeamId: null,
    }
  },

  computed: {
    dialog: {
      get () {
        return this.value
      },

      set (value) {
        this.$emit('input', value)
      },
    },

    filteredTeamRecords () {
      if (!this.recordFilter) return this.teamRecords
      return this.teamRecords.filter(r => r.name.toLowerCase().includes(this.recordFilter.toLowerCase()))
    },

    isTeamGlobal () {
      return this.selectedTeam?.is_global
    },

    selectedTeamName () {
      return this.selectedTeam?.name || ''
    },

    teamRecords () {
      return this.selectedTeam?.record_for_options || []
    },

    ...mapGetters([
      'currentUser',
    ]),
  },

  watch: {
    value (newValue) {
      if (!newValue) return
      this.selectedTeamId = this.currentUser.active_team_id
    },
  },

  methods: {
    onSelectRecord (record) {
      const ref = this.$router.resolve({
        name: 'record',
        params: {
          id: record.public_id,
        },
      })

      window.open(ref.href, '_blank')
    },

    onSelectTeam () {
      this.isProcessing = true

      this.changeCurrentTeam(this.selectedTeamId).
        then(_ => {
          this.displayAlert('success', 'Team updated successfully')
          this.$router.replace({
            path: this.$route.path,
            query: {},
          })
          this.$router.go(0)

          this.dialog = false
        }).catch(e => {
          this.displayAlert('error', parseErrorResponseMessage(e))
        }).finally(_ => {
          this.isProcessing = false
        })
    },

    ...mapActions(USER_NAMESPACE, [
      'changeCurrentTeam',
    ]),
  },
}
</script>
